import { useState } from 'react';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import isValidURL from 'components/Dashboard/utilities/isValidURL';
import idsWithoutImages from './idsWithoutImages';

interface KeyUpdateImageProps {
  item: { source?: string | null; imageUrl?: string | null; imageId?: number | null };
  className?: string;
}

interface Candidate {
  url: string;
  isLogo: boolean;
  crossOrigin?: 'anonymous';
  backgroundColor?: string;
}

const SAMPLE_SIZE = 16;

const getPlainEdgeColour = (img: HTMLImageElement) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const { data } = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const corners = [
      [1, 1],
      [SAMPLE_SIZE - 2, 1],
      [1, SAMPLE_SIZE - 2],
      [SAMPLE_SIZE - 2, SAMPLE_SIZE - 2],
    ].map(([x, y]) => data.slice((y * SAMPLE_SIZE + x) * 4, (y * SAMPLE_SIZE + x) * 4 + 4));
    const [first] = corners;
    const isPlain = corners.every(
      (pixel) => pixel[3] > 250 && [0, 1, 2].every((channel) => Math.abs(pixel[channel] - first[channel]) <= 16)
    );
    return isPlain ? `rgb(${first[0]}, ${first[1]}, ${first[2]})` : null;
  } catch {
    return null;
  }
};

const KeyUpdateImage = ({ item, className = '' }: KeyUpdateImageProps) => {
  const { replacementOfUrl, isBadResolutionLogo } = useLogoUrlReplacement();
  const [failedUrls, setFailedUrls] = useState<string[]>([]);
  const [edgeColour, setEdgeColour] = useState<{ url: string; colour: string | null }>();
  const { source, imageUrl, imageId } = item;

  const candidates = [
    source &&
      isValidURL(source) &&
      !isBadResolutionLogo(new URL(source).hostname) && {
        url: replacementOfUrl({ url: `https://img.logo.dev/${new URL(source).hostname}` }),
        isLogo: true,
        crossOrigin: 'anonymous',
      },
    imageUrl && { url: imageUrl, isLogo: false },
    !!imageId &&
      !idsWithoutImages.includes(imageId) && {
        url: `https://www.parliamenttoday.com/image.php?imageid=${imageId}&mode=1`,
        isLogo: false,
      },
    { url: `${import.meta.env.VITE_APP_CDNURL}/images/favicon.png`, isLogo: true, backgroundColor: '#121f34' },
  ].filter(Boolean) as Candidate[];
  const candidate = candidates.find(({ url }) => !failedUrls.includes(url));

  if (!candidate) return null;
  const { url, isLogo, crossOrigin } = candidate;
  const backgroundColor = (edgeColour?.url === url && edgeColour.colour) || candidate.backgroundColor;
  return (
    <div className='d-none d-sm-block'>
      <img
        className={`content-image ${isLogo ? 'key-update-logo' : ''} ${className}`}
        style={backgroundColor ? { backgroundColor } : undefined}
        src={url}
        alt={isLogo ? 'logotype' : 'key update image'}
        crossOrigin={crossOrigin}
        onLoad={(e) => crossOrigin && setEdgeColour({ url, colour: getPlainEdgeColour(e.currentTarget) })}
        onError={() => setFailedUrls((urls) => [...urls, url])}
      />
    </div>
  );
};

export default KeyUpdateImage;
